import React from 'react';
import { connect } from 'react-redux';
import Arrow from '@mui/icons-material/TrendingFlat';
import { List, ListItem, ListItemIcon } from '@mui/material';

import { createScrollbarStyling } from '@paragon/themes';

import { getPassOrFailColorFromAnalysisSet } from '../../../capacityColors';
import { RootState } from '../../../store';
import { getActiveComponentDesign } from '../../../store/selectors/getActiveComponentDesignSelector';
import { getAnalysisHistoryForActiveComponentDesign } from '../../../store/selectors/getAnalysisHistoryForActiveComponentDesignSelector';
import { isViewingOtherOrganizationsComponent } from '../../../store/selectors/viewOnlyModeSelectors';
import { doWorkingChangesExistForActiveComponentDesign } from '../../../store/selectors/workingChangesSelectors';
import AnalysisHistoryRecordSubPanel from '../subpanels/AnalysisHistoryRecordStatement';
import WorkingChangesStatement from '../subpanels/WorkingChangesStatement';

class AnalysisHistoryPanel extends React.Component<ReturnType<typeof mapStateToProps>> {
  renderCommits() {
    if (!this.props.analysisHistory) {
      return [];
    }

    const results: React.JSX.Element[] = [];

    const { hasWorkingChanges, viewingOtherOrganizationsComponent } = this.props;
    const workingChangesAreActive = !this.props.activeAnalysisSetGuid;

    if (hasWorkingChanges && (!viewingOtherOrganizationsComponent || workingChangesAreActive)) {
      results.push(<WorkingChangesStatement key="working-changes" active={workingChangesAreActive} />);
    }

    for (const analysisHistoryRecord of this.props.analysisHistory) {
      // Shared Component Sets being viewed by the owning organization should still only show the active analysis set
      if (
        this.props.isInSharedComponentSetContext &&
        analysisHistoryRecord.analysisSetGuid !== this.props.activeAnalysisSetGuid
      ) {
        continue;
      }

      const analysisSet = this.props.analysisSets.find((set) => set.guid === analysisHistoryRecord.analysisSetGuid);
      const analysisSetColor = analysisSet ? getPassOrFailColorFromAnalysisSet(analysisSet) : 'white';

      const active = analysisHistoryRecord.analysisSetGuid === this.props.activeAnalysisSetGuid;

      if (!viewingOtherOrganizationsComponent || active) {
        if (results.length > 0) {
          results.push(
            <ListItem sx={{ m: 0 }} key={`${analysisHistoryRecord.analysisSetGuid}-arrow`}>
              <ListItemIcon>
                <Arrow sx={{ transform: 'rotate(-90deg)', color: '#9e9e9e' }} />
              </ListItemIcon>
            </ListItem>,
          );
        }

        results.push(
          <AnalysisHistoryRecordSubPanel
            active={analysisHistoryRecord.analysisSetGuid === this.props.activeAnalysisSetGuid}
            analysisHistoryRecord={analysisHistoryRecord}
            isWorkingSet={!!analysisSet && this.props.currentComponentDesign === analysisSet.componentDesign}
            key={analysisHistoryRecord.analysisSetGuid}
            commitColor={analysisSetColor}
          />,
        );
      }
    }

    return results;
  }

  render() {
    return (
      <List dense disablePadding sx={[{ flex: 1, minHeight: 0, overflowY: 'auto' }, createScrollbarStyling]}>
        {this.renderCommits()}
      </List>
    );
  }
}

const mapStateToProps = (state: RootState) => {
  const activeComponentDesign = getActiveComponentDesign(state);

  return {
    analysisHistory: getAnalysisHistoryForActiveComponentDesign(state),
    hasWorkingChanges: doWorkingChangesExistForActiveComponentDesign(state),

    viewingOtherOrganizationsComponent: isViewingOtherOrganizationsComponent(state),
    isInSharedComponentSetContext: state.application.openContext?.type === 'shared-component-set',

    analysisSets: state.analysisSets.sets,
    activeAnalysisSetGuid: state.analysisSets.activeAnalysisSetGuid,
    currentComponentDesign: activeComponentDesign ? state.componentDesigns.data[activeComponentDesign.guid] : null,
  };
};

export default connect(mapStateToProps)(AnalysisHistoryPanel);
